/* ==========================================================================
   Zapatos Obi — header.js
   Menú móvil, overlay de búsqueda, cabecera fija y rotación de anuncios.
   ========================================================================== */
(function () {
  'use strict';

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait || 10);
    };
  }

  function trapInitialFocus(container) {
    var focusable = container.querySelector('a, button, input, [tabindex]');
    if (focusable) focusable.focus();
  }

  class HeaderController extends HTMLElement {
    connectedCallback() {
      this.mobileMenu = this.querySelector('[data-mobile-menu]');
      this.searchOverlay = this.querySelector('[data-search-overlay]');
      this.searchToggle = this.querySelector('[data-search-toggle]');
      this.menuToggle = this.querySelector('[data-menu-toggle]');

      this._bindEvents();
      this._initSticky();
      this._initAnnouncement();
    }

    _bindEvents() {
      var self = this;

      this.querySelectorAll('[data-menu-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggleMenu(true);
        });
      });
      this.querySelectorAll('[data-menu-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggleMenu(false);
        });
      });

      this.querySelectorAll('[data-search-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggleSearch();
        });
      });
      this.querySelectorAll('[data-search-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggleSearch(false);
        });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          self.toggleMenu(false);
          self.toggleSearch(false);
        }
      });

      /* selector de idioma: cerrar al hacer clic fuera */
      document.addEventListener('click', function (e) {
        var lang = self.querySelector('[data-lang-menu]');
        if (lang && lang.open && !lang.contains(e.target)) {
          lang.open = false;
        }
      });

      /* mega-menú con pestañas: la familia bajo el cursor muestra su panel.
         En la cuadrícula de letras (MARCAS) el hover cambiaría de letra sin
         querer al cruzar el ratón: ahí solo se activa con clic. */
      this.querySelectorAll('[data-mega-tabs]').forEach(function (wrap) {
        var tabs = wrap.querySelectorAll('[data-mega-tab]');
        var panels = wrap.querySelectorAll('[data-mega-panel]');
        var clickOnly = !!wrap.querySelector('.header__mega-tabs--letters');
        tabs.forEach(function (tab) {
          var activate = function () {
            var idx = tab.getAttribute('data-mega-tab');
            tabs.forEach(function (t) {
              t.classList.toggle('is-active', t === tab);
            });
            panels.forEach(function (p) {
              p.classList.toggle('is-active', p.getAttribute('data-mega-panel') === idx);
            });
          };
          if (!clickOnly) tab.addEventListener('mouseenter', activate);
          tab.addEventListener('focus', activate);
          tab.addEventListener('click', function (e) {
            /* las pestañas sin destino (letras de MARCAS) solo activan su panel */
            if (tab.getAttribute('href') === '#') {
              e.preventDefault();
              activate();
            }
          });
        });
      });
    }

    toggleMenu(open) {
      if (!this.mobileMenu) return;
      this.mobileMenu.hidden = !open;
      document.documentElement.classList.toggle('overflow-hidden', open);
      if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', String(open));
      if (open) trapInitialFocus(this.mobileMenu);
      else if (this.menuToggle) this.menuToggle.focus();
    }

    toggleSearch(force) {
      if (!this.searchOverlay) return;
      var root = document.documentElement;
      var isOpen = root.classList.contains('search-drawer-open');
      var open = typeof force === 'boolean' ? force : !isOpen;
      if (open === isOpen) return;
      root.classList.toggle('search-drawer-open', open);
      if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', String(open));
      if (open) {
        var input = this.searchOverlay.querySelector('input[type="search"]');
        if (input) input.focus();
      } else if (this.searchToggle) {
        this.searchToggle.focus();
      }
    }

    _initSticky() {
      var self = this;

      /* Altura de la cabecera fija como variable CSS: otros elementos
         sticky (p. ej. la barra de filtros) se pegan justo debajo. */
      var setOffset = function () {
        var sticky = self.classList.contains('header-wrapper--sticky');
        document.documentElement.style.setProperty(
          '--obi-sticky-offset',
          sticky ? self.offsetHeight + 'px' : '0px'
        );
      };
      setOffset();
      window.addEventListener('resize', debounce(setOffset, 100));

      if (!this.classList.contains('header-wrapper--sticky')) return;
      window.addEventListener(
        'scroll',
        debounce(function () {
          self.classList.toggle('is-scrolled', window.scrollY > 40);
        }, 10),
        { passive: true }
      );
    }

    _initAnnouncement() {
      var bar = document.querySelector('[data-announcement]');
      if (!bar || bar.dataset.autorotate !== 'true') return;
      var items = Array.prototype.slice.call(bar.querySelectorAll('.announcement__item'));
      if (items.length < 2) return;
      var index = 0;
      var speed = (parseInt(bar.dataset.speed, 10) || 5) * 1000;
      setInterval(function () {
        items[index].classList.remove('is-active');
        index = (index + 1) % items.length;
        items[index].classList.add('is-active');
      }, speed);
    }
  }

  if (!customElements.get('header-controller')) {
    customElements.define('header-controller', HeaderController);
  }

  /* Columnas del footer: acordeón en móvil, siempre abiertas en PC.
     Se aplica solo al cruzar el breakpoint (no en cada resize de scroll). */
  function initFooterDisclosure() {
    var items = document.querySelectorAll('[data-footer-disclosure]');
    if (!items.length) return;
    var mq = window.matchMedia('(max-width: 749px)');
    var apply = function () {
      items.forEach(function (d) {
        d.open = !mq.matches;
      });
    };
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
  }

  if (document.readyState !== 'loading') initFooterDisclosure();
  else document.addEventListener('DOMContentLoaded', initFooterDisclosure);
})();
